import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { ShippingOption } from '../../types';
import './AdminShipping.css';

interface ShippingFormData {
  name: string;
  carrier: string;
  basePrice: string;
  pricePerItem: string;
  estimatedDays: string;
  isActive: boolean;
}

const AdminShipping: React.FC = () => {
  const [options, setOptions] = useState<ShippingOption[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingOption, setEditingOption] = useState<ShippingOption | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [formData, setFormData] = useState<ShippingFormData>({
    name: '',
    carrier: '',
    basePrice: '',
    pricePerItem: '',
    estimatedDays: '',
    isActive: true,
  });
  const { success, error: showError } = useToast();

  const fetchOptions = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<ShippingOption[]>('/admin/shipping/options');
      setOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch shipping options:', error);
      showError('Failed to load shipping options');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      if (!formData.name.trim() || !formData.carrier.trim() || !formData.basePrice || !formData.pricePerItem || !formData.estimatedDays) {
        showError('Please fill in all required fields.');
        return;
      }

      setSaving(true);
      const data = {
        ...formData,
        basePrice: parseFloat(formData.basePrice),
        pricePerItem: parseFloat(formData.pricePerItem),
        estimatedDays: parseInt(formData.estimatedDays),
      };

      if (editingOption) {
        await api.put(`/admin/shipping/options/${editingOption.id}`, data);
        success('Shipping option updated');
      } else {
        await api.post('/admin/shipping/options', data);
        success('Shipping option created');
      }

      setShowForm(false);
      setEditingOption(null);
      setFormData({
        name: '',
        carrier: '',
        basePrice: '',
        pricePerItem: '',
        estimatedDays: '',
        isActive: true,
      });
      fetchOptions();
    } catch (error) {
      showError('Failed to save shipping option');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (option: ShippingOption): void => {
    setEditingOption(option);
    setFormData({
      name: option.name,
      carrier: option.carrier,
      basePrice: option.basePrice.toString(),
      pricePerItem: option.pricePerItem.toString(),
      estimatedDays: option.estimatedDays.toString(),
      isActive: (option as any).isActive !== undefined ? (option as any).isActive : true,
    });
    setShowForm(true);
  };

  if (loading) {
    return <div className="loading">Loading shipping options...</div>;
  }

  return (
    <div className="admin-shipping">
      <div className="container">
        <div className="page-header">
          <div className="page-header-left">
            <Link to="/admin" className="back-button">
              <i className="fas fa-arrow-left"></i> Back to Admin Panel
            </Link>
            <h1 className="page-title">Shipping Options</h1>
          </div>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingOption(null);
              setFormData({
                name: '',
                carrier: '',
                basePrice: '',
                pricePerItem: '',
                estimatedDays: '',
                isActive: true,
              });
            }}
            className="btn btn-primary"
          >
            Add Shipping Option
          </button>
        </div>

        {showForm && (
          <div className="shipping-form-card">
            <h2>{editingOption ? 'Edit' : 'Add'} Shipping Option</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Carrier</label>
                <input
                  type="text"
                  value={formData.carrier}
                  onChange={(e) => setFormData({ ...formData, carrier: e.target.value })}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Base Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.basePrice}
                    onChange={(e) => setFormData({ ...formData, basePrice: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Price Per Item</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.pricePerItem}
                    onChange={(e) => setFormData({ ...formData, pricePerItem: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Estimated Days</label>
                <input
                  type="number"
                  value={formData.estimatedDays}
                  onChange={(e) => setFormData({ ...formData, estimatedDays: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving && <span className="btn-spinner" aria-hidden="true"></span>}
                  {saving ? 'Saving...' : (editingOption ? 'Update' : 'Create')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingOption(null);
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="shipping-options-grid">
          {options.map((option) => (
            <div key={option.id} className="shipping-option-card">
              <div className="option-header">
                <h3>{option.name}</h3>
                <span className={`status-badge ${(option as any).isActive !== false ? 'active' : 'inactive'}`}>
                  {(option as any).isActive !== false ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="option-details">
                <p><strong>Carrier:</strong> {option.carrier}</p>
                <p><strong>Base Price:</strong> ${option.basePrice.toFixed(2)}</p>
                <p><strong>Price Per Item:</strong> ${option.pricePerItem.toFixed(2)}</p>
                <p><strong>Estimated Days:</strong> {option.estimatedDays}</p>
              </div>
              <button
                onClick={() => handleEdit(option)}
                className="btn btn-outline btn-small"
              >
                Edit
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminShipping;
